import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import AuthenticationTabs from '@/components/AuthenticationTabs';
import PasskeySignInButton from '@/components/Button/PasskeySignInButton';
import ContextualSignInPage from '@/components/ContextualSignInPage';
import TextLink from '@/components/TextLink';
import WechatBindingContinuation from '@/components/WechatSignIn/BindingContinuation';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';
import { getContextualSignInPage } from '@/utils/contextual-sign-in-page';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();

  const { signInMethods, signUpMethods, signInMode, singleSignOnEnabled, passkeySignIn } =
    useSieMethods();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    /**
     * Check if the user has agreed to the terms and privacy policy before navigating to the SSO page
     * when the policy is set to `Manual`
     */
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  /* Hide footers when showing Single Sign On form */
  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {
        // Single Sign On footer
        singleSignOnEnabled && (
          <>
            <div className={styles.singleSignOn}>
              {t('description.use')}{' '}
              <TextLink text="action.single_sign_on" onClick={handleSsoNavigation} />
            </div>
            {
              /**
               * If only SSO sign-in methods are available, display the agreement checkbox when the agreement policy is `Manual`.
               */
              signInMethods.length === 0 && agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                <TermsAndPrivacyCheckbox className={styles.checkboxForSsoOnly} />
              )
            }
          </>
        )
      }
      {
        // Create Account footer
        signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
          <div className={styles.createAccount}>
            {t('description.no_account')}{' '}
            <TextLink replace to="/register" text="action.create_account" />
          </div>
        )
      }
      {passkeySignIn?.enabled && passkeySignIn.showPasskeyButton && <PasskeySignInButton />}
    </>
  );
};

const SignIn = () => {
  const { signInMethods, signInMode } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  const contextual = getContextualSignInPage();
  if (contextual) {
    return <ContextualSignInPage settings={contextual} />;
  }
  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  return (
    <WechatBindingContinuation
      fallback={
        <LandingPageLayout isHeaderHidden title="description.sign_in_to_your_account">
          <AuthenticationTabs active="sign-in" />
          <WebAuthnContextProvider>
            <SingleSignOnFormModeContextProvider>
              <Main signInMethods={signInMethods} />
              <SignInFooters />
            </SingleSignOnFormModeContextProvider>
          </WebAuthnContextProvider>
          {
            // Only show terms and privacy links for sign in page if the agree to terms policy is `Automatic` or `ManualRegistrationOnly`
            agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
              <TermsAndPrivacyLinks className={styles.terms} />
            )
          }
        </LandingPageLayout>
      }
    />
  );
};

export default SignIn;
